export const environmentNames = ['prod', 'sandbox', 'phitest', 'dev'] as const;
export type Env = (typeof environmentNames)[number];

export const isValidEnv = (maybeEnv: string): maybeEnv is Env =>
  environmentNames.includes(maybeEnv as never);

// Hostnames that point at a developer's own machine rather than a shared environment.
export const localDevHostnames = ['localhost', '127.0.0.1', 'ctw.localhost'] as const;

// Returns false, rather than throwing, for a non-string input.
export const isLocalDevHostname = (hostnameWithOptionalPort: string): boolean =>
  typeof hostnameWithOptionalPort === 'string' &&
  (localDevHostnames as ReadonlyArray<string>).includes(hostnameWithOptionalPort.split(':')[0]);

const envPatternMappings: Record<Env, Array<RegExp>> = {
  dev: [/^([a-z][a-z.-]*)\.dev\.(zushealth|zusapi)\.com$/i, /^preview(.*)$/i],
  phitest: [/^([a-z][a-z.-]*)\.(phitest|phi-test)\.(zushealth|zusapi)\.com$/i],
  sandbox: [/^([a-z][a-z.-]*)\.sandbox\.(zushealth|zusapi)\.com$/i],
  prod: [/^([a-z][a-z.-]*)\.(zushealth|zusapi)\.com$/i],
} as const;

interface GetEnvFromCurrentHostnameOptions {
  fallbackEnv?: Env;
}

export function getEnvFromCurrentHostname(options: GetEnvFromCurrentHostnameOptions = {}): Env {
  return getEnvFromHostname(window.location.hostname, options);
}

/*
 * Resolves the env for an explicit hostname (with optional port) instead of `window.location`.
 */
export function getEnvFromHostname(
  hostnameWithOptionalPort: string,
  { fallbackEnv }: GetEnvFromCurrentHostnameOptions = {},
): Env {
  const hostname = hostnameWithOptionalPort.split(':')[0];

  for (const [env, patterns] of Object.entries(envPatternMappings)) {
    for (const pattern of patterns) {
      if (pattern.test(hostname) && isValidEnv(env)) {
        return env;
      }
    }
  }

  if (!fallbackEnv) {
    throw new Error(
      'Could not determine application environment from URL and no fallbackEnv was provided',
    );
  }

  return fallbackEnv;
}

export const currentHostnameIsLocalDevEnvironment = () =>
  isLocalDevHostname(window.location.hostname);

export const envValueOrThrow = (envVarName: string): string => {
  const value = process.env[envVarName];
  if (!value) {
    throw new Error(`${envVarName} environment variable is not set`);
  }
  return value;
};

export const validEnvOrUndefined = (maybeEnv: string | undefined): Env | undefined => {
  if (maybeEnv === undefined) {
    return undefined;
  }

  const env = maybeEnv.toLowerCase();

  switch (env) {
    case 'prod':
    case 'production':
      return 'prod';
    case 'dev':
    case 'development':
      return 'dev';
    case 'phitest':
    case 'phi-test':
      return 'phitest';
    case 'sandbox':
      return 'sandbox';
    default:
      return undefined;
  }
};

// Unset or empty selects sandbox, the environment CI validates codegen against.
export const codegenEnvOrThrow = (rawEnv: string | undefined): Env => {
  if (rawEnv === undefined || rawEnv === '') {
    return 'sandbox';
  }

  const env = validEnvOrUndefined(rawEnv);
  if (!env) {
    throw new Error(
      `Invalid CODEGEN_ENV "${rawEnv}". Allowed values: ${environmentNames.join(', ')} (or unset).`,
    );
  }

  return env;
};

export type IntegrationTestEnv = Extract<Env, 'dev' | 'sandbox'>;

export const integrationTestEnvOrThrow = (rawEnv: string | undefined): IntegrationTestEnv => {
  if (rawEnv === undefined || rawEnv === '') {
    return 'sandbox';
  }

  if (rawEnv === 'dev' || rawEnv === 'sandbox') {
    return rawEnv;
  }

  throw new Error(
    `Invalid integration test ENV "${rawEnv}". Allowed values: "dev", "sandbox" (or unset).`,
  );
};

// The Auth0 tenant's canonical domain. The Management API is registered only here, not on the
// custom login domain returned by `getAuth0Domain`.
export function getAuth0TenantDomain(env: Env): string {
  switch (env) {
    case 'prod':
      return 'zus-production.us.auth0.com';
    case 'dev':
      return 'zus-dev.us.auth0.com';
    case 'sandbox':
      return 'zus-sandbox.us.auth0.com';
    case 'phitest':
      return 'zus-phi-test.us.auth0.com';
    default:
      return 'zus-dev.us.auth0.com';
  }
}

export function getAuthServiceOauthTokenUrl(env: Env): string {
  return `https://${getAuth0TenantDomain(env)}/oauth/token`;
}

// The custom domain users log in through.
export const getAuth0Domain = (env: Env): string =>
  env === 'prod' ? 'auth.zusapi.com' : `auth.${env}.zusapi.com`;

export const getAuth0IssuerBaseUrl = (env: Env): string => `https://${getAuth0Domain(env)}`;

export const getBuilderConfigTableName = (env: Env): string => `${env}-builder-config`;

export const getAppFeedbackSubmissionTableName = (env: Env): string =>
  `${env}-app-feedback-submission`;

export const getImpersonationBrokerCodesTableName = (env: Env): string =>
  `${env}-impersonation-broker-codes`;

export const snowflakeDatabaseName = 'ODS_EXPORTS';
export const snowflakeWarehouseName = 'CTW';
export const sigmaOrganizationSlug = 'zushealth';

export function getZusApiBaseUrl(env: Env): string {
  switch (env) {
    case 'prod':
      return 'https://api.zusapi.com';
    case 'dev':
      return 'https://api.dev.zusapi.com';
    case 'sandbox':
      return 'https://api.sandbox.zusapi.com';
    case 'phitest':
      return 'https://api.phitest.zusapi.com';
    default:
      return 'https://api.dev.zusapi.com';
  }
}

/*
 * The Backend-for-frontend API base URL
 */
export function getBffApiBaseUrl(env: Env): string {
  if (typeof window === 'undefined') {
    return bffApiBaseUrlForEnv(env);
  }

  return getBffApiBaseUrlForHostname(window.location.hostname, env);
}

/*
 * Resolves the BFF API base URL for an explicit hostname instead of `window.location`.
 */
export function getBffApiBaseUrlForHostname(hostname: string, env: Env): string {
  const hostnameWithoutPort = hostname.split(':')[0];

  if (isLocalDevHostname(hostnameWithoutPort)) {
    return 'http://localhost:7552';
  }

  if (hostnameWithoutPort.startsWith('preview-pr-')) {
    return `https://${hostnameWithoutPort.split('.')[0]}.api.app.dev.zushealth.com`;
  }

  return bffApiBaseUrlForEnv(env);
}

function bffApiBaseUrlForEnv(env: Env): string {
  switch (env) {
    case 'prod':
      return 'https://api.app.zushealth.com';
    case 'dev':
      return 'https://api.app.dev.zushealth.com';
    case 'sandbox':
      return 'https://api.app.sandbox.zushealth.com';
    case 'phitest':
      return 'https://api.app.phitest.zushealth.com';
    default:
      return 'https://api.app.dev.zushealth.com';
  }
}

/*
 * The CDA Renderer API base URL
 */
export function getCdaRendererBaseUrl(env: Env): string {
  const windowIsLocalDev =
    typeof window !== 'undefined' && isLocalDevHostname(window.location.hostname);
  if (windowIsLocalDev) {
    return 'http://localhost:7552';
  }

  switch (env) {
    case 'prod':
      return 'https://cda-renderer-pilot.zusapi.com';
    case 'dev':
      return 'https://cda-renderer-pilot.dev.zusapi.com';
    case 'sandbox':
      return 'https://cda-renderer-pilot.sandbox.zusapi.com';
    case 'phitest':
      return 'https://cda-renderer-pilot.phitest.zusapi.com';
    default:
      return 'https://cda-renderer-pilot.dev.zusapi.com';
  }
}

type ApiService =
  | 'auth-service'
  | 'auth-thingy'
  | 'data-transformer'
  | 'ehr-hooks'
  | 'forms-data'
  | 'fhir'
  | 'fhir-async'
  | 'fqs'
  | 'lens';

const apiServicesThatUsePhitestSubdomainWithoutADash: Array<ApiService> = [
  'auth-service',
  'auth-thingy',
  'data-transformer',
  'ehr-hooks',
  'fhir',
  'fhir-async',
  'fqs',
  'lens',
];

interface GetApiServiceBaseUrlOptions {
  service: ApiService;
  env: Env;
}

export function getApiServiceBaseUrl({ env, service }: GetApiServiceBaseUrlOptions): string {
  const phiTestUrlEnv = apiServicesThatUsePhitestSubdomainWithoutADash.includes(service)
    ? 'phitest'
    : 'phi-test';

  switch (env) {
    case 'prod':
      return `https://${service}.zusapi.com`;
    case 'dev':
      return `https://${service}.dev.zusapi.com`;
    case 'sandbox':
      return `https://${service}.sandbox.zusapi.com`;
    case 'phitest':
      return `https://${service}.${phiTestUrlEnv}.zusapi.com`;
    default:
      return `https://${service}.dev.zusapi.com`;
  }
}

interface GetAIApiServiceBaseUrlOptions {
  env: Env;
}

export function getAiServiceBaseUrl({ env }: GetAIApiServiceBaseUrlOptions): string {
  switch (env) {
    case 'prod':
      return 'https://ai.zusapi.com';
    case 'dev':
      return 'https://ai.dev.zusapi.com';
    case 'sandbox':
      return 'https://ai.sandbox.zusapi.com';
    case 'phitest':
      return 'https://ai.phitest.zusapi.com';
    default:
      return 'https://ai.dev.zusapi.com';
  }
}

export function getZapBaseUrl(env: Env): string {
  if (isLocalDevHostname(window.location.hostname)) {
    return `http://${window.location.hostname}:5173`;
  }

  if (window.location.hostname.startsWith('preview-pr-')) {
    return `https://${window.location.hostname.split('.')[0]}.zap.dev.zushealth.com`;
  }

  switch (env) {
    case 'prod':
      return 'https://zap.zushealth.com';
    case 'dev':
      return 'https://zap.dev.zushealth.com';
    case 'sandbox':
      return 'https://zap.sandbox.zushealth.com';
    case 'phitest':
      return 'https://zap.phitest.zushealth.com';
    default:
      return 'https://zap.dev.zushealth.com';
  }
}

export function getUpi2ApiBaseUrl(env: Env): string {
  switch (env) {
    case 'prod':
      return 'https://upi2.zusapi.com';
    case 'dev':
      return 'https://upi2.dev.zusapi.com';
    case 'sandbox':
      return 'https://upi2.sandbox.zusapi.com';
    case 'phitest':
      return 'https://upi2.phitest.zusapi.com';
    default:
      return 'https://upi2.dev.zusapi.com';
  }
}

export function getStandaloneBaseUrl(env: Env): string {
  if (typeof window !== 'undefined' && isLocalDevHostname(window.location.hostname)) {
    return `http://${window.location.hostname}:3000`;
  }

  if (typeof window !== 'undefined' && window.location.hostname.startsWith('preview-pr-')) {
    return `https://${window.location.hostname.split('.')[0]}.app.dev.zushealth.com`;
  }

  switch (env) {
    case 'prod':
      return 'https://app.zushealth.com';
    case 'dev':
      return 'https://app.dev.zushealth.com';
    case 'sandbox':
      return 'https://app.sandbox.zushealth.com';
    case 'phitest':
      return 'https://app.phitest.zushealth.com';
    default:
      return 'https://app.dev.zushealth.com';
  }
}

// First-party relay in front of Amplitude's ingestion and Guides & Surveys hosts.
export function getAmplitudeProxyBaseUrl(env: Env): string {
  if (typeof window !== 'undefined' && window.location.hostname.startsWith('preview-pr-')) {
    return `https://${window.location.hostname.split('.')[0]}.relay.app.dev.zushealth.com`;
  }

  switch (env) {
    case 'prod':
      return 'https://relay.app.zushealth.com';
    case 'dev':
      return 'https://relay.app.dev.zushealth.com';
    case 'sandbox':
      return 'https://relay.app.sandbox.zushealth.com';
    case 'phitest':
      return 'https://relay.app.phitest.zushealth.com';
    default:
      return 'https://relay.app.dev.zushealth.com';
  }
}
